<template>
    <div class="container">
        <h2>Edit User</h2>
        <div>
            <router-link to="/users" class="btn btn-link">
                <i class="fas fa-arrow-left"></i> Back to Users List
            </router-link>
        </div>

        <div class="my-3 d-flex justify-content-start">
            <router-link to="#" @click.prevent="confirmResetPassword" class="btn btn-danger">
                Reset Password
            </router-link>
        </div>

        <form @submit.prevent="submitForm" class="form-horizontal col-sm-6">
            <div class="form-group row">
                <label for="firstName" class="col-sm-4 col-form-label text-right">First Name *</label>
                <div class="col-sm-8">
                    <input type="text" class="form-control" id="firstName" v-model="user.firstName" required>
                </div>
            </div>
            <div class="form-group row">
                <label for="lastName" class="col-sm-4 col-form-label text-right">Last Name *</label>
                <div class="col-sm-8">
                    <input type="text" class="form-control" id="lastName" v-model="user.lastName" required>
                </div>
            </div>
            <div class="form-group row">
                <label for="email" class="col-sm-4 col-form-label text-right">Email *</label>
                <div class="col-sm-8">
                    <input type="email" class="form-control" id="email" v-model="user.email" required disabled>
                </div>
            </div>
            <div class="form-group row">
                <label for="phone" class="col-sm-4 col-form-label text-right">Phone *</label>
                <div class="col-sm-8">
                    <input type="text" class="form-control" id="phone" v-model="user.phone" required>
                </div>
            </div>
            <div class="form-group row">
                <label for="roles" class="col-sm-4 col-form-label text-right">Roles</label>
                <div class="col-sm-8">
                    <div v-for="role in allRoles" :key="role.id" class="form-check">
                        <input
                            type="checkbox"
                            class="form-check-input"
                            :id="role.id"
                            :value="role.id"
                            v-model="selectedRoles">
                        <label class="form-check-label" :for="role.id">{{ role.code }} ({{ role.description }})</label>
                    </div>
                </div>
            </div>
            <div class="form-group row">
                <label for="mfaRequired" class="col-sm-4 col-form-label text-right">MFA Required *</label>
                <div class="col-sm-8">
                    <select class="form-control" id="mfaRequired" v-model="user.mfaRequired">
                        <option :value="true">Yes</option>
                        <option :value="false">No</option>
                    </select>
                </div>
            </div>
            <div class="form-group row">
                <label for="status" class="col-sm-4 col-form-label text-right">Status *</label>
                <div class="col-sm-8">
                    <select class="form-control" id="status" v-model="user.active">
                        <option :value="true">Active</option>
                        <option :value="false">Inactive</option>
                    </select>
                </div>
            </div>
            <div class="form-group row">
                <div class="col-sm-8 offset-sm-4">
                    <button type="submit" class="btn btn-primary">
                        <output v-if="loading" class="spinner-border spinner-border-sm" aria-hidden="true"></output>
                        Save
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import api from '@/api';
import eventBus from '@/utils/eventBus';
import { checkAuth } from '@/utils/auth';

export default {
    data() {
        return {
            user: {
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                roles: '',
                active: true,
                mfaRequired: false
            },
            allRoles: [],
            selectedRoles: [],
            loading: false
        };
    },
    async mounted() {
        if (!(await checkAuth())) {
            eventBus.emit('show-alert', { message: 'You need to log in to access this page.', type: 'danger' });
            this.$router.push('/');
        } else {
            await this.fetchRoles();
            await this.fetchUser();
        }
    },
    methods: {
        async fetchRoles() {
            try {
                const response = await api.get('/v1/roles', {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
                    },
                    params: { active: 'true' }
                });

                if (response.status === 200) {
                    this.allRoles = response.data.data.roles;
                }
            } catch (error) {
                if (error.response.data.message) {
                    eventBus.emit('show-alert', { message: error.response.data.message, type: 'danger' });
                } else {
                    eventBus.emit('show-alert', { message: 'Failed to fetch roles.', type: 'danger' });
                }
            }
        },
        async fetchUser() {
            try {
                const response = await api.get(`/v1/users/${this.$route.params.userId}`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
                    }
                });

                if (response.status === 200) {
                    this.user = response.data.data.user;
                    if (Array.isArray(this.user.userRoles)) {
                        this.selectedRoles = this.user.userRoles.map(role => role.id);
                    }
                    console.log("Selected Roles:", this.selectedRoles);
                }
            } catch (error) {
                console.error('Error fetching user:', error);
                if (error.response.data.message) {
                    eventBus.emit('show-alert', { message: error.response.data.message, type: 'danger' });
                } else {
                    eventBus.emit('show-alert', { message: 'Failed to fetch user.', type: 'danger' });
                }
            }
        },
        async submitForm() {
            this.loading = true;
            if (!(await checkAuth())) {
                eventBus.emit('show-alert', { message: 'You need to log in to access this page.', type: 'danger' });
                this.$router.push('/');
            } else {
                try {
                    const response = await api.put(`/v1/users/${this.$route.params.userId}`, {
                        firstName: this.user.firstName,
                        lastName: this.user.lastName,
                        email: this.user.email,
                        phone: this.user.phone,
                        roles: Array.from(document.querySelectorAll('input[type="checkbox"]:checked')).map(role => ({ id: role.value })),
                        active: this.user.active,
                        mfaRequired: this.user.mfaRequired
                    }, {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('accessToken')}`
                        }
                    });

                    if (response.status === 200) {
                        eventBus.emit('show-alert', { message: 'User updated successfully.', type: 'success' });
                        this.$nextTick(() => {
                            this.$router.push('/users');
                        });
                    }
                } catch (error) {
                    if (error.response.data.message) {
                        eventBus.emit('show-alert', { message: error.response.data.message, type: 'danger' });
                    } else {
                        eventBus.emit('show-alert', { message: 'Failed to update user.', type: 'danger' });
                    }
                }
            }
            this.loading = false;
        },
        confirmResetPassword() {
            if (window.confirm('Are you sure you want to reset the password for this user?')) {
                this.resetPassword();
            }
        },
        async resetPassword() {
            const redirectUrl = `${window.location.origin}/#/set-password`;
            try {
                const response = await api.put('/v1/users/reset-password', {
                    email: this.user.email,
                    redirectUrl
                });

                if (response.status === 200) {
                    eventBus.emit('show-alert', { message: response.data.message, type: 'success' });
                }
            } catch (error) {
                if (error.response.data.message) {
                    eventBus.emit('show-alert', { message: error.response.data.message, type: 'danger' });
                } else {
                    eventBus.emit('show-alert', { message: 'Failed to initiate password reset.', type: 'danger' });
                }
            }
        }
    }
};
</script>

<style>
.form-horizontal .form-group {
    margin-top: 1rem;
    margin-bottom: 1rem;
}
</style>